import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import Cookies from 'js-cookie';

import { HSM_BACKEND_URL } from '@/constants/environment';
import { invalidateSession } from '@/services/auth/invalidate-session';

// Security note — JWT in a JS-accessible cookie.
// The `jwt` cookie is NOT httpOnly (historical Cliengo pattern; the
// shared login domain writes a cookie that both this SPA and the
// other Cliengo apps read with js-cookie). Any XSS in this app can
// exfiltrate the token. For an admin-only tool behind the Cliengo
// SSO the risk is accepted; if the session model gets hardened
// (httpOnly + same-site server cookies), update this read site and
// redirect-to-login.ts together.

// Backend can stall. Cap the wait so the UI surfaces an error state
// instead of spinning forever.
const REQUEST_TIMEOUT_MS = 15_000;

function attachJwt(config: InternalAxiosRequestConfig) {
  const jwt = Cookies.get('jwt');

  if (jwt) {
    config.headers.Authorization = `Bearer ${jwt}`;
  }

  return config;
}

export function handleResponseError(error: AxiosError) {
  if (error.response?.status === 401) {
    // Drop the local session; the guard listens to the event and
    // drops to NoJwtScreen without a browser reload.
    invalidateSession();
  }
  return Promise.reject(error);
}

function createClient(baseURL: string): AxiosInstance {
  const instance = axios.create({
    baseURL,
    timeout: REQUEST_TIMEOUT_MS,
    headers: { 'Content-Type': 'application/json' },
  });

  instance.interceptors.request.use(attachJwt);
  instance.interceptors.response.use((r) => r, handleResponseError);

  return instance;
}

export const hsmBackendClient = createClient(HSM_BACKEND_URL);
